/**
 * useNotificationListView — pure view derivations for the
 * SidePanelNotificationList: filtering by search, splitting calls /
 * regulars, sorting by time, grouping by app, and bucketing by date.
 *
 * Extracted from `SidePanelNotificationList` in PR #411's review
 * follow-up. The original component computed nine memoized derivations
 * inline; this hook gathers them so the parent's render path consumes
 * a single named view object and the derivations can be reasoned about
 * (and tested) in isolation.
 *
 * Also owns the midnight tick: a `setTimeout` to msUntilMidnight that
 * forces the timeline buckets to re-derive at the date boundary so
 * "Today" / "Yesterday" labels stay accurate without a page refresh.
 * The tick is internal state — the parent doesn't need to know about
 * it.
 *
 * Pure: no callbacks, no side effects beyond the midnight timer, no
 * DOM access. Output is a normal object — consumers can destructure
 * what they need.
 */

import type { ConversationThread, DecryptedNotification } from "@reflecto/client-core";
import { useEffect, useMemo, useState } from "react";
import {
  type AppGroup,
  buildPrivacyGroups,
  buildTimelineBuckets,
  groupByApp,
  type PrivacyAppGroup,
  type TimelineBucket,
} from "./helpers";

export interface UseNotificationListViewOptions {
  notifications: DecryptedNotification[];
  conversations: ConversationThread[];
  isGrouped: boolean;
  searchQuery: string;
  /** "Today" label — used by buildTimelineBuckets for date headers. */
  todayLabel: string;
  /** "Yesterday" label — used by buildTimelineBuckets for date headers. */
  yesterdayLabel: string;
}

export interface NotificationListView {
  /**
   * Call-type notifications (category === "call"). Render first in
   * the list regardless of grouping mode, and are NOT subject to the
   * search filter (calls are short-lived; surfacing them is more
   * important than letting search hide them).
   */
  callNotifications: DecryptedNotification[];
  /**
   * Non-call notifications excluding conversation-linked entries.
   * Used both for the search-filter input AND for the empty-state
   * "are there any cards at all?" check.
   */
  regularNotifications: DecryptedNotification[];
  /** `regularNotifications` after the case-insensitive search filter. */
  filteredRegular: DecryptedNotification[];
  /** Conversations after the case-insensitive search filter. */
  filteredConversations: ConversationThread[];
  /**
   * `filteredRegular` sorted newest-first. Computed in both modes;
   * `null` here only signals consumers should not read it in grouped
   * mode — `appGroups` is the authoritative grouped-view source.
   */
  sorted: DecryptedNotification[] | null;
  /**
   * Per-app groups for the grouped render mode. `null` in timeline
   * mode. Returned as the raw `Map` so the parent preserves
   * insertion order when iterating.
   */
  appGroups: Map<string, AppGroup> | null;
  /**
   * Date-bucketed merged feed for the timeline render mode. Empty
   * array in grouped mode.
   */
  timelineBuckets: TimelineBucket[];
  /**
   * Union of app-package keys for the grouped render mode: every
   * app with regular notifications OR a conversation. Empty in
   * timeline mode. Order is insertion order, which matches the
   * grouped JSX iteration order — keyboard nav relies on this
   * lockstep.
   */
  allGroupKeys: string[];
  /**
   * `filteredRegular.length + filteredConversations.length`. Used by
   * the parent to label the Clear button as "Clear all N matches"
   * during an active search.
   */
  matchCount: number;
  /**
   * One entry per app package for privacy mode: app name, total item
   * count, and the count noun-kind. Recency-sorted newest-first.
   * Derived from the unfiltered source splits (search is hidden in
   * privacy mode). Computed unconditionally; only read when
   * `publicMode` is on.
   */
  privacyGroups: PrivacyAppGroup[];
}

export function useNotificationListView({
  notifications,
  conversations,
  isGrouped,
  searchQuery,
  todayLabel,
  yesterdayLabel,
}: UseNotificationListViewOptions): NotificationListView {
  // Midnight tick: re-render at the date boundary so "Today"/"Yesterday"
  // labels stay accurate without a page refresh. The state itself is
  // never read outside this hook — only its identity matters as a
  // memo-invalidation key.
  const [tick, setTick] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: tick triggers re-scheduling the next midnight timer
  useEffect(() => {
    const now = new Date();
    const msUntilMidnight =
      new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() - now.getTime();
    const timer = setTimeout(() => setTick((t) => t + 1), msUntilMidnight + 100);
    return () => clearTimeout(timer);
  }, [tick]);

  const callNotifications = useMemo(
    () => notifications.filter((n) => n.category === "call"),
    [notifications]
  );
  // Regular notifications: non-call, excluding legacy conversation-linked
  // entries (new conversation messages live only in ConversationThread).
  const regularNotifications = useMemo(
    () => notifications.filter((n) => n.category !== "call" && !n.conversationKey),
    [notifications]
  );

  // Search applies to plain (non-conversation) notifications and conversation threads.
  const filteredRegular = useMemo(() => {
    // Cache the lowercased query once to avoid ~3 redundant .toLowerCase()
    // calls per notification on every keystroke-triggered re-memo.
    const q = searchQuery.toLowerCase();
    return regularNotifications.filter(
      (n) =>
        !q ||
        n.app.toLowerCase().includes(q) ||
        n.title.toLowerCase().includes(q) ||
        n.body.toLowerCase().includes(q)
    );
  }, [regularNotifications, searchQuery]);

  const filteredConversations = useMemo(() => {
    // Same cache for conversations — avoids repeated conversions over
    // title/app/message text on every keystroke.
    const q = searchQuery.toLowerCase();
    return conversations.filter(
      (c) =>
        !q ||
        c.title.toLowerCase().includes(q) ||
        c.app.toLowerCase().includes(q) ||
        c.messages.some((m) => m.text.toLowerCase().includes(q))
    );
  }, [conversations, searchQuery]);

  // Derived view state, all memoized on `filteredRegular` /
  // `filteredConversations` / `isGrouped`. Without these memos,
  // unrelated state changes (collapsedApps, tick, searchQuery
  // keystrokes) would re-run the O(n log n) sort + grouping on
  // every render.
  const timeSorted = useMemo(
    () => [...filteredRegular].sort((a, b) => b.timestamp - a.timestamp),
    [filteredRegular]
  );
  // `timeSorted` is already memoized, so this ternary is reference-stable
  // (always `===` itself) without an extra `useMemo` — wrapping would be
  // pure slot bookkeeping with zero computation savings.
  const sorted = isGrouped ? null : timeSorted;
  const appGroups = useMemo(
    () => (isGrouped ? groupByApp(timeSorted) : null),
    [isGrouped, timeSorted]
  );
  // `tick` is in the dep array but not read inside — it's the midnight
  // invalidation trigger. `buildTimelineBuckets` calls `getDateLabel`
  // which calls `new Date()`; without `tick` in the deps, date labels
  // ("Today" / "Yesterday") would never refresh after midnight.
  // biome-ignore lint/correctness/useExhaustiveDependencies: tick is the midnight re-label trigger
  const timelineBuckets = useMemo(
    () =>
      isGrouped
        ? []
        : buildTimelineBuckets(sorted ?? [], filteredConversations, {
            today: todayLabel,
            yesterday: yesterdayLabel,
          }),
    [isGrouped, sorted, filteredConversations, todayLabel, yesterdayLabel, tick]
  );
  const allGroupKeys: string[] = useMemo(
    () =>
      isGrouped
        ? Array.from(
            new Set([
              ...(appGroups ? Array.from(appGroups.keys()) : []),
              ...filteredConversations.map((c) => c.packageName),
            ])
          )
        : [],
    [isGrouped, appGroups, filteredConversations]
  );

  const matchCount = filteredRegular.length + filteredConversations.length;

  const privacyGroups = useMemo(
    () => buildPrivacyGroups(callNotifications, regularNotifications, conversations),
    [callNotifications, regularNotifications, conversations]
  );

  return {
    callNotifications,
    regularNotifications,
    filteredRegular,
    filteredConversations,
    sorted,
    appGroups,
    timelineBuckets,
    allGroupKeys,
    matchCount,
    privacyGroups,
  };
}
